import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';

const UserRoleManagement = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Form states
    const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState('');
    const [expiresAt, setExpiresAt] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [usersRes, rolesRes, userRolesRes] = await Promise.all([
                fetch(`${API_BASE_URL}/users`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE_URL}/roles`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE_URL}/userRoles`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const [usersData, rolesData, userRolesData] = await Promise.all([
                usersRes.json(),
                rolesRes.json(),
                userRolesRes.json()
            ]);

            setUsers(usersData);
            setRoles(rolesData);
            setUserRoles(userRolesData);
        } catch (err) {
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleAssignRole = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validate required fields
        if (!selectedUser || !selectedRole) {
            setError('Please select both user and role');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const requestBody = {
                USER_ID: selectedUser,
                ROLE_ID: selectedRole,
                EXPIRES_AT: expiresAt || null
            };
            
            console.log('Making POST request to assign role:', requestBody);
            
            const response = await fetch(`${API_BASE_URL}/userRoles/assign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });
            
            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);

            const data = await response.json();
            console.log('Response data:', data);

            if (response.ok) {
                setSuccess('Role assigned successfully');
                setShowAssignRoleModal(false);
                setSelectedUser(null);
                setSelectedRole('');
                setExpiresAt('');
                fetchData();
            } else {
                console.error('Error response:', data);
                setError(data.message || `Failed to assign role (Status: ${response.status})`);
            }
        } catch (err) {
            console.error('Network error:', err);
            setError(`Network error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveRole = async (userRoleId) => {
        if (!window.confirm('Are you sure you want to remove this role?')) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/userRoles/remove/${userRoleId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setSuccess('Role removed successfully');
                fetchData();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to remove role');
            }
        } catch (err) {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    };

    const getUserRoles = (userId) => {
        return userRoles.filter(ur => ur.USER_ID._id === userId && ur.IS_ACTIVE);
    };

    const getRoleName = (roleId) => {
        const role = roles.find(r => r._id === roleId);
        return role ? role.ROLE_NAME : 'Unknown';
    };

    const getDashboardName = (roleId) => {
        const role = roles.find(r => r._id === roleId);
        return role ? role.DASHBOARD_NAME : 'Unknown';
    };

    if (loading && users.length === 0) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="user-role-management">
            <div className="header">
                <h2>User Role Management</h2>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowAssignRoleModal(true)}
                >
                    Assign Role
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="users-table">
                <table>
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Roles</th>
                            <th>Dashboards</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => {
                            const userRolesList = getUserRoles(user._id);
                            return (
                                <tr key={user._id}>
                                    <td>
                                        <div>
                                            <strong>{user.firstName} {user.lastName}</strong>
                                            <br />
                                            <small>@{user.username}</small>
                                        </div>
                                    </td>
                                    <td>{user.email}</td>
                                    <td>
                                        {userRolesList.length > 0 ? (
                                            <div className="roles-list">
                                                {userRolesList.map(userRole => (
                                                    <span key={userRole._id} className="role-badge">
                                                        {getRoleName(userRole.ROLE_ID._id)}
                                                        <button
                                                            className="remove-role-btn"
                                                            onClick={() => handleRemoveRole(userRole._id)}
                                                            title="Remove role"
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="no-roles">No roles assigned</span>
                                        )}
                                    </td>
                                    <td>
                                        {userRolesList.length > 0 ? (
                                            <div className="dashboards-list">
                                                {[...new Set(userRolesList.map(ur => getDashboardName(ur.ROLE_ID._id)))].map(dashboard => (
                                                    <span key={dashboard} className="dashboard-badge">
                                                        {dashboard}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="no-dashboards">No dashboard access</span>
                                        )}
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => {
                                                setSelectedUser(user._id);
                                                setShowAssignRoleModal(true);
                                            }}
                                        >
                                            Add Role
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Assign Role Modal */}
            {showAssignRoleModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3>Assign Role to User</h3>
                            <button 
                                className="close-btn"
                                onClick={() => setShowAssignRoleModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <form>
                            <div className="form-group">
                                <label>User:</label>
                                <select
                                    value={selectedUser || ''}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    required
                                >
                                    <option value="">Select User</option>
                                    {users.map(user => (
                                        <option key={user._id} value={user._id}>
                                            {user.firstName} {user.lastName} (@{user.username})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Role:</label>
                                <select
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                    required
                                >
                                    <option value="">Select Role</option>
                                    {roles.map(role => (
                                        <option key={role._id} value={role._id}>
                                            {role.ROLE_NAME} ({role.DASHBOARD_NAME})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Expires At (Optional):</label>
                                <input
                                    type="datetime-local"
                                    value={expiresAt}
                                    onChange={(e) => setExpiresAt(e.target.value)}
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowAssignRoleModal(false)}>
                                    Cancel
                                </button>
                                <button type="button" onClick={handleAssignRole} disabled={loading}>
                                    {loading ? 'Assigning...' : 'Assign Role'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                .user-role-management {
                    padding: 20px;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .users-table {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                }

                th, td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #eee;
                }

                th {
                    background: #f8f9fa;
                    font-weight: 600;
                }

                .roles-list, .dashboards-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                }

                .role-badge, .dashboard-badge {
                    background: #007bff;
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                }

                .dashboard-badge {
                    background: #28a745;
                }

                .remove-role-btn {
                    background: none;
                    border: none;
                    color: white;
                    cursor: pointer;
                    font-size: 16px;
                    line-height: 1;
                    padding: 0;
                    width: 16px;
                    height: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .remove-role-btn:hover {
                    background: rgba(255,255,255,0.2);
                    border-radius: 50%;
                }

                .no-roles, .no-dashboards {
                    color: #6c757d;
                    font-style: italic;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }

                .modal {
                    background: white;
                    border-radius: 8px;
                    padding: 0;
                    width: 90%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    border-bottom: 1px solid #eee;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    padding: 0;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .form-group {
                    margin-bottom: 15px;
                    padding: 0 20px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 5px;
                    font-weight: 500;
                }

                .form-group select,
                .form-group input {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                }

                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                    padding: 20px;
                    border-top: 1px solid #eee;
                }

                .modal-actions button {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                }

                .modal-actions button[type="button"] {
                    background: #6c757d;
                    color: white;
                }

                .modal-actions button[type="submit"] {
                    background: #007bff;
                    color: white;
                }

                .modal-actions button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .error-message {
                    background: #f8d7da;
                    color: #721c24;
                    padding: 10px;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    border: 1px solid #f5c6cb;
                }

                .success-message {
                    background: #d4edda;
                    color: #155724;
                    padding: 10px;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    border: 1px solid #c3e6cb;
                }

                .btn {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                    text-decoration: none;
                    display: inline-block;
                }

                .btn-primary {
                    background: #007bff;
                    color: white;
                }

                .btn-secondary {
                    background: #6c757d;
                    color: white;
                }

                .btn-sm {
                    padding: 4px 8px;
                    font-size: 12px;
                }

                .loading {
                    text-align: center;
                    padding: 40px;
                    font-size: 18px;
                }
            `}</style>
        </div>
    );
};

export default UserRoleManagement;
